import { Badge } from '@/components/ui/badge'
import { AlertCircle } from 'lucide-react'

/**
 * Help content specific to ad groups
 */
export function AdGroupHelp() {
    return (
        <>
            <h3 className="font-medium text-lg mb-2">Publishing Ad Groups</h3>
            <div className="space-y-5">
                <div>
                    <h4 className="font-medium text-base mb-2">Publishing Options</h4>
                    <div className="bg-secondary/30 p-4 rounded-md border border-border mb-4">
                        <ul className="grid gap-3">
                            <li className="flex items-start gap-3">
                                <Badge variant="outline" className="mt-0.5">Option 1</Badge>
                                <div>
                                    <span className="font-medium">Copy Headlines & Descriptions:</span> Export your ad content for use in ad platforms
                                </div>
                            </li>
                            <li className="flex items-start gap-3">
                                <Badge variant="outline" className="mt-0.5">Option 2</Badge>
                                <div>
                                    <span className="font-medium">Preview Format:</span> View how your ads will appear on different platforms
                                </div>
                            </li>
                            <li className="flex items-start gap-3">
                                <Badge variant="outline" className="mt-0.5">Option 3</Badge>
                                <div>
                                    <span className="font-medium">Share Collection:</span> Share all your ad variations with team members
                                </div>
                            </li>
                        </ul>
                    </div>
                </div>

                <div>
                    <h4 className="font-medium text-base mb-2">Step-by-step Publishing Guide</h4>
                    <ol className="list-decimal pl-5 space-y-3 mb-4">
                        <li>
                            <span className="font-medium">Finalize your ad content:</span> Review all headlines and descriptions
                            <div className="pl-5 mt-1 text-sm text-muted-foreground">
                                <p>Use the <Badge variant="outline" className="mx-1 font-normal">Improve Content</Badge> feature to refine your ad copy</p>
                            </div>
                        </li>
                        <li>
                            <span className="font-medium">Set status to Published:</span> Open the content settings and change status to 
                            <Badge className="ml-1 mr-1 font-normal">Published</Badge>
                            <div className="pl-5 mt-1 text-sm">
                                <p className="text-muted-foreground">This marks the ad group as ready for use and enables all sharing features</p>
                            </div>
                        </li>
                        <li>
                            <span className="font-medium">Choose your export method:</span>
                            <div className="pl-5 mt-1 space-y-2">
                                <div>
                                    <p className="font-medium text-sm">For copying all headlines and descriptions:</p>
                                    <ol className="list-[lower-alpha] pl-5 text-sm text-muted-foreground">
                                        <li>Click <Badge variant="outline" className="mx-1 font-normal">Share & Embed</Badge> in the toolbar</li>
                                        <li>Select <span className="font-semibold">Copy Markdown</span> to get all content in a structured format</li>
                                        <li>Paste into a document or your ad platform's bulk upload tool</li>
                                    </ol>
                                </div>
                                <div>
                                    <p className="font-medium text-sm">For viewing ad previews:</p>
                                    <ol className="list-[lower-alpha] pl-5 text-sm text-muted-foreground">
                                        <li>Click <Badge variant="outline" className="mx-1 font-normal">Share & Embed</Badge> in the toolbar</li>
                                        <li>Click <span className="font-semibold">Open as Webpage</span></li>
                                        <li>View how your ads will appear in various formats</li>
                                    </ol>
                                </div>
                                <div>
                                    <p className="font-medium text-sm">For sharing with your team:</p>
                                    <ol className="list-[lower-alpha] pl-5 text-sm text-muted-foreground">
                                        <li>Click <Badge variant="outline" className="mx-1 font-normal">Share & Embed</Badge> in the toolbar</li>
                                        <li>Click <span className="font-semibold">Copy Preview URL</span></li>
                                        <li>Share this URL with team members for review or implementation</li>
                                    </ol>
                                </div>
                            </div>
                        </li>
                        <li>
                            <span className="font-medium">Implement in ad platforms:</span> Upload to your advertising accounts
                            <div className="pl-5 mt-1 text-sm text-muted-foreground">
                                <p>Add creative assets, set targeting parameters, and configure campaign settings in your ad platform</p>
                            </div>
                        </li>
                    </ol>
                </div>

                <div>
                    <h4 className="font-medium text-base mb-2">Making Updates After Publishing</h4>
                    <div className="bg-secondary/30 p-4 rounded-md border border-border mb-4">
                        <ol className="list-decimal pl-5 space-y-2">
                            <li>Return to this page and make your desired changes to headlines or descriptions</li>
                            <li>Use <Badge variant="outline" className="mx-1 font-normal">Improve Content</Badge> or edit directly</li>
                            <li>A new version will be created automatically</li>
                            <li>For ads already running, you'll need to update them in your ad platform</li>
                            <li>Consider A/B testing new versions against existing ones to measure performance</li>
                        </ol>
                    </div>
                </div>
                    
                <div className="bg-amber-50 dark:bg-amber-950/30 p-4 rounded-md border border-amber-200 dark:border-amber-800 mb-4">
                    <h4 className="font-medium text-amber-800 dark:text-amber-400 flex items-center gap-2 mb-2">
                        <AlertCircle className="h-4 w-4" />
                        Online Advertising Best Practices
                    </h4>
                    <ul className="list-disc pl-5 space-y-1 text-sm text-amber-700 dark:text-amber-300">
                        <li>Test multiple headline and description combinations to find the best performers</li>
                        <li>Keep headlines clear, concise, and focused on a single value proposition</li>
                        <li>Include a specific call-to-action in your descriptions</li>
                        <li>Ensure landing pages match the messaging in your ads for consistency</li>
                        <li>Monitor performance metrics and optimize campaigns regularly</li>
                        <li>Check that all ads comply with the target platform's policies and character limits</li>
                    </ul>
                </div>
            </div>
        </>
    )
}